
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { DemoFormData } from "../validation-schemas";
import { useTranslation } from "react-i18next";
import { Users, Building2, CalendarClock } from "lucide-react";

interface FormSectionProps {
  form: UseFormReturn<DemoFormData>;
}

export const PersonalInfoSection = ({ form }: FormSectionProps) => {
  const { t } = useTranslation('contact');
  return (
    <div className="animate-fade-in [--animate-delay:200ms]">
      <div className="flex items-center gap-2 text-lg font-medium text-brand-blue dark:text-brand-orange mb-4">
        <Users className="w-5 h-5" />
        Informations personnelles
      </div>
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.firstName")}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t("form.firstName_example")}
                  className="focus-visible:ring-brand-orange/50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.lastName")}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t("form.lastName_example")}
                  className="focus-visible:ring-brand-orange/50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.email")}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t("form.email_example")}
                  className="focus-visible:ring-brand-orange/50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export const CompanyInfoSection = ({ form }: FormSectionProps) => {
  const { t } = useTranslation('contact');
  return (
    <div className="animate-fade-in [--animate-delay:400ms]">
      <div className="flex items-center gap-2 text-lg font-medium text-brand-blue dark:text-brand-orange mb-4">
        <Building2 className="w-5 h-5" />
        Informations de l'entreprise
      </div>
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="company"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.company")}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t("form.company_example")}
                  className="focus-visible:ring-brand-orange/50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="jobTitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.position")}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t("form.position_example")}
                  className="focus-visible:ring-brand-orange/50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="teamSize"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.teamSize")}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t("form.teamSize_example")}
                  className="focus-visible:ring-brand-orange/50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export const DemoPreferencesSection = ({ form }: FormSectionProps) => {
  const { t } = useTranslation('contact');
  return (
    <div className="animate-fade-in [--animate-delay:600ms]">
      <div className="flex items-center gap-2 text-lg font-medium text-brand-blue dark:text-brand-orange mb-4">
        <CalendarClock className="w-5 h-5" />
        Préférences de démonstration
      </div>
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="interest"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.interest")}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t("form.interest_example")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-background">
                  <SelectItem value="skills-mapping">
                    {t("form.interests.skills-mapping")}
                  </SelectItem>
                  <SelectItem value="job-evolution">
                    {t("form.interests.job-evolution")}
                  </SelectItem>
                  <SelectItem value="hr-analytics">
                    {t("form.interests.hr-analytics")}
                  </SelectItem>
                  <SelectItem value="talent-mobility">
                    {t("form.interests.talent-mobility")}
                  </SelectItem>
                  <SelectItem value="skills-framework">
                    {t("form.interests.skills-framework")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="preferredDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.preferredDate")}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t("form.preferredDate_example")}
                  className="focus-visible:ring-brand-orange/50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
